/**
 *  @class
 *  @function SearchForm
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.is_drawer = this.dataset.drawer;
      this.drawer = this.closest('#Search-Drawer');
      this.form = this.querySelector('form');
      this.button = document.querySelectorAll('.thb-quick-search');
      this.input = this.querySelector('.search-field');
      this.defaultTab = this.querySelector('.side-panel-content--initial');
      this.inlineDefault = this.querySelector('.header-search-start');
      this.predictiveSearchResults = this.is_drawer ? this.querySelector('.side-panel-content--has-tabs') : this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');
      this.promotionTemplate = this.querySelector('.thb-predictive-search--promotion-template');
      this.promotionHydrated = false;
      this.cache = new Map();
      this.cacheLimit = 50;
      this.lastSearchTerm = '';
      this.cartResume = this.querySelector('[data-search-cart-resume]');
      this.cartItems = this.querySelector('[data-search-cart-items]');
      this.cartLoadedAt = 0;
      this.cartLoading = false;
      this.currentProductElement = this.querySelector('[data-search-current-product]');
      this.currentProduct = this.parseCurrentProduct();
      this.recentStorageKey = this.cartResume?.dataset.recentStorageKey || 'goldenmaple_recent_products_v1';
      this.recentMaxAge = (Number.parseInt(this.cartResume?.dataset.recentMaxAgeDays, 10) || 30) * 86400000;

      this.setupEventListeners();
      this.trackCurrentProduct();
    }

    hydratePromotion() {
      if (!this.promotionTemplate) {
        return;
      }
      this.promotionTemplate.parentNode.insertBefore(
        this.promotionTemplate.content,
        this.promotionTemplate
      );
      this.promotionTemplate.remove();
      this.promotionTemplate = null;
      this.promotionHydrated = true;
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
      this.input.addEventListener('focus', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      this.button.forEach((item) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          this.hydratePromotion();
          if (this.is_drawer) {
            OverlayManager.open(this.drawer);
            this.hydrateCartResume();
            setTimeout(() => {
              this.input.focus({
                preventScroll: true
              });
            }, 100);
          } else {
            document.body.classList.add('open-cc');
          }
          return false;
        });
      });

      if (!this.is_drawer) {
        this.onCcClickBound = this.close.bind(this);
        this.onKeyUpBound = (e) => {
          if (e.code && e.code.toUpperCase() === 'ESCAPE') {
            this.close();
          }
        };
        this.cc?.addEventListener('click', this.onCcClickBound);
        document.addEventListener('keyup', this.onKeyUpBound);
      }
    }

    disconnectedCallback() {
      if (this.onKeyUpBound) {
        document.removeEventListener('keyup', this.onKeyUpBound);
      }
      if (this.onCcClickBound && this.cc) {
        this.cc.removeEventListener('click', this.onCcClickBound);
      }
    }

    parseCurrentProduct() {
      if (!this.currentProductElement) {
        return null;
      }
      try {
        return JSON.parse(this.currentProductElement.textContent);
      } catch (error) {
        return null;
      }
    }

    getSafeProductUrl(value) {
      if (!value) {
        return null;
      }
      try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin || !/(^|\/)products\//.test(url.pathname)) {
          return null;
        }
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (error) {
        return null;
      }
    }

    getSafeImageUrl(value) {
      if (!value) {
        return null;
      }
      try {
        const url = new URL(value, window.location.origin);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') {
          return null;
        }
        return url.href;
      } catch (error) {
        return null;
      }
    }

    readRecentProducts() {
      try {
        const stored = JSON.parse(window.localStorage.getItem(this.recentStorageKey) || '[]');
        if (!Array.isArray(stored)) {
          return [];
        }

        const now = Date.now();
        const products = stored.filter((item) => {
          const viewedAt = Number(item?.viewedAt);
          return item?.id && item?.title && this.getSafeProductUrl(item.url) && Number.isFinite(viewedAt) && now - viewedAt <= this.recentMaxAge;
        }).slice(0, 12);

        if (products.length !== stored.length) {
          window.localStorage.setItem(this.recentStorageKey, JSON.stringify(products));
        }
        return products;
      } catch (error) {
        return [];
      }
    }

    trackCurrentProduct() {
      if (!this.currentProduct?.id || !this.currentProduct?.title) {
        return;
      }

      const url = this.getSafeProductUrl(this.currentProduct.url);
      if (!url) {
        return;
      }

      try {
        const product = {
          id: String(this.currentProduct.id),
          url,
          title: String(this.currentProduct.title),
          image: this.getSafeImageUrl(this.currentProduct.image),
          imageAlt: String(this.currentProduct.imageAlt || this.currentProduct.title),
          priceCents: Number(this.currentProduct.priceCents) || 0,
          currency: String(this.currentProduct.currency || ''),
          viewedAt: Date.now()
        };
        const products = this.readRecentProducts().filter((item) => String(item.id) !== product.id);
        window.localStorage.setItem(this.recentStorageKey, JSON.stringify([product, ...products].slice(0, 12)));
      } catch (error) {
        return;
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.lastSearchTerm = '';
        this.showDefaultContent();
        return;
      }
      if (!this.predictiveSearchResults) return;
      if (!this.is_drawer) {
        this.open();
      }
      if (searchTerm === this.lastSearchTerm) {
        return;
      }
      this.lastSearchTerm = searchTerm;
      this.showSearchResults();
      this.getSearchResults(searchTerm);
    }

    showDefaultContent() {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.remove('active');
        this.predictiveSearchResults.innerHTML = '';
        if (!this.is_drawer) {
          this.predictiveSearchResults.setAttribute('inert', '');
        }
      }
      if (this.defaultTab) {
        this.defaultTab.style.display = '';
        if (this.is_drawer) this.hydrateCartResume();
      }
      if (this.inlineDefault) {
        this.inlineDefault.style.display = '';
      }
      if (!this.is_drawer) {
        if (document.activeElement === this.input && this.inlineDefault) {
          this.open();
          this.hydrateCartResume();
        } else {
          this.close();
        }
      }
    }

    showSearchResults() {
      if (this.defaultTab) {
        this.defaultTab.style.display = 'none';
      }
      if (this.inlineDefault) {
        this.inlineDefault.style.display = 'none';
        this.inlineDefault.setAttribute('inert', '');
      }
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.add('active');
        this.predictiveSearchResults.removeAttribute('inert');
      }
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    getSearchResults(searchTerm) {
      const cached = this.cache.get(searchTerm);
      if (cached) {
        this.renderSearchResults(cached);
        return;
      }

      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.add('loading');
      }

      fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query,page&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`)
        .then((response) => {
          if (this.predictiveSearchResults) {
            this.predictiveSearchResults.classList.remove('loading');
          }
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

          if (this.cache.size >= this.cacheLimit) {
            this.cache.delete(this.cache.keys().next().value);
          }
          this.cache.set(searchTerm, resultsMarkup);

          if (this.getQuery() === searchTerm) this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          throw error;
        });
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      const submitBtn = this.predictiveSearchResults.querySelector('#search-results-submit');
      if (submitBtn) {
        if (!this.is_drawer) {
          submitBtn.classList.remove('button');
        }
        submitBtn.addEventListener('click', () => {
          this.form.submit();
        });
      }
    }

    async hydrateCartResume() {
      if (!this.cartResume || !this.cartItems || this.cartLoading) {
        return;
      }
      if (!this.is_drawer && Date.now() - this.cartLoadedAt < 30000) {
        return;
      }

      this.cartLoading = true;
      let cart = { items: [], currency: this.currentProduct?.currency || '' };
      try {
        const response = await fetch(this.cartResume.dataset.cartUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) {
          throw new Error(response.status);
        }

        cart = await response.json();
      } catch (error) {
        if (this.cartResume.dataset.resumeMode === 'recent') {
          this.cartResume.hidden = true;
          this.cartLoading = false;
          return;
        }
        cart.items = [];
      } finally {
        if (this.cartLoading) this.renderResumeShopping(cart);
        this.cartLoadedAt = Date.now();
        this.cartLoading = false;
      }
    }

    renderResumeShopping(cart) {
      const itemLimit = Number.parseInt(this.cartResume.dataset.itemLimit, 10) || 2;
      const cartProducts = [];
      const seenProductIds = new Set();

      if (Array.isArray(cart.items)) {
        cart.items.forEach((item) => {
          const id = String(item.product_id || '');
          const url = this.getSafeProductUrl(item.url);
          if (!id || !url || seenProductIds.has(id)) {
            return;
          }
          seenProductIds.add(id);
          cartProducts.push({
            id,
            url,
            title: item.product_title,
            image: this.getSafeImageUrl(item.featured_image?.url || item.image),
            imageAlt: item.featured_image?.alt || item.product_title || '',
            priceCents: item.final_price || item.price || 0,
            currency: cart.currency || ''
          });
        });
      }

      const currentProductId = String(this.currentProduct?.id || '');
      const recentProducts = this.readRecentProducts().filter((item) => {
        const id = String(item.id);
        if (seenProductIds.has(id) || (!this.is_drawer && id === currentProductId)) {
          return false;
        }
        if (!this.is_drawer && cart.currency && item.currency && cart.currency !== item.currency) {
          return false;
        }
        seenProductIds.add(id);
        return true;
      });

      const products = (this.cartResume.dataset.resumeMode === 'recent' ? recentProducts : [...cartProducts, ...recentProducts]).slice(0, itemLimit);
      this.cartItems.replaceChildren();
      if (!products.length) {
        this.cartResume.hidden = true;
        return;
      }

      const currency = cart.currency || products.find((item) => item.currency)?.currency || 'USD';
      let formatter;
      try {
        formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency
        });
      } catch (error) {
        formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: 'USD'
        });
      }

      const fragment = document.createDocumentFragment();
      products.forEach((item) => {
        const link = document.createElement('a');
        link.className = 'header-search-start__product';
        link.href = item.url;

        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = item.imageAlt || item.title || '';
          image.width = 72;
          image.height = 72;
          image.loading = 'lazy';
          link.appendChild(image);
        } else {
          link.classList.add('header-search-start__product--no-image');
        }

        const details = document.createElement('span');
        details.className = 'header-search-start__product-details';

        const title = document.createElement('span');
        title.className = 'header-search-start__product-title';
        title.textContent = item.title;

        const meta = document.createElement('span');
        meta.className = 'header-search-start__product-meta';
        meta.textContent = formatter.format((Number(item.priceCents) || 0) / 100);

        details.append(title, meta);
        link.appendChild(details);
        fragment.appendChild(link);
      });

      this.cartItems.appendChild(fragment);
      this.cartResume.hidden = false;
    }

    close() {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.setAttribute('inert', '');
      }
      if (this.inlineDefault) {
        this.inlineDefault.setAttribute('inert', '');
      }
      document.body.classList.remove('open-search');
    }
    open() {
      document.body.classList.add('open-search');
      if (this.predictiveSearchResults) {
        if (this.is_drawer || this.getQuery().length) {
          this.predictiveSearchResults.removeAttribute('inert');
        } else {
          this.predictiveSearchResults.setAttribute('inert', '');
        }
      }
      if (this.inlineDefault && !this.getQuery().length) {
        this.inlineDefault.removeAttribute('inert');
      }
    }
  }
  customElements.define('search-form', SearchForm);
}
